// src/components/PerformanceCompare.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ComposedChart, Area, XAxis, YAxis, ResponsiveContainer } from "recharts";
import { useBufferAudioSync } from "../hooks/useBufferAudioSync";

type Props = {
  // audio
  refUrl: string;
  stuUrl: string;

  // envelopes
  refEnvelope?: number[];
  stuEnvelope?: number[];

  // analysis meta
  duration?: number; // sec
  lagMs?: number; // lag ref<->stu (ms) (même convention que AudioLab)
  bpm?: number;
  lang?: "fr" | "en";

  // OPTIONAL videos for Match
  refVideoUrl?: string;
  stuVideoUrl?: string;
};

type MixMode = "ref" | "mix" | "stu";

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v));

const StaticSessionChart = React.memo(
  ({
    data,
    mixMode,
    totalDuration,
  }: {
    data: any[];
    mixMode: MixMode;
    totalDuration: number;
  }) => {
    return (
      <ResponsiveContainer width="100%" height="100%" minHeight={260} debounce={100}>
        <ComposedChart data={data} margin={{ top: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="gradRefPC" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00f2ff" stopOpacity={0.6} />
              <stop offset="100%" stopColor="#00f2ff" stopOpacity={0.1} />
            </linearGradient>
            <linearGradient id="gradStuPC" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ff2e2e" stopOpacity={0.8} />
              <stop offset="100%" stopColor="#ff2e2e" stopOpacity={0.2} />
            </linearGradient>
          </defs>

          <XAxis dataKey="time" hide type="number" domain={[0, totalDuration]} />
          <YAxis domain={[-5, 5]} hide />

          {(mixMode === "ref" || mixMode === "mix") && (
            <>
              <Area
                type="monotone"
                dataKey="refP"
                stroke="#00f2ff"
                strokeWidth={1}
                fill="url(#gradRefPC)"
                isAnimationActive={false}
                baseValue={0}
              />
              <Area
                type="monotone"
                dataKey="refN"
                stroke="#00f2ff"
                strokeWidth={1}
                fill="url(#gradRefPC)"
                isAnimationActive={false}
                baseValue={0}
              />
            </>
          )}

          {(mixMode === "stu" || mixMode === "mix") && (
            <>
              <Area
                type="monotone"
                dataKey="stuP"
                stroke="#ff2e2e"
                strokeWidth={2}
                fill="url(#gradStuPC)"
                isAnimationActive={false}
                baseValue={0}
              />
              <Area
                type="monotone"
                dataKey="stuN"
                stroke="#ff2e2e"
                strokeWidth={2}
                fill="url(#gradStuPC)"
                isAnimationActive={false}
                baseValue={0}
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }
);

export const PerformanceCompare: React.FC<Props> = ({
  refUrl,
  stuUrl,
  refEnvelope = [],
  stuEnvelope = [],
  duration,
  lagMs = 0,
  bpm = 90,
  lang = "fr",
  refVideoUrl,
  stuVideoUrl,
}) => {
  const hasRefAudio = !!refUrl && refUrl.trim().length > 0;
  const hasStuAudio = !!stuUrl && stuUrl.trim().length > 0;

  const hasRefVideo = !!refVideoUrl && refVideoUrl.trim().length > 0;
  const hasStuVideo = !!stuVideoUrl && stuVideoUrl.trim().length > 0;

  const refVid = useRef<HTMLVideoElement>(null);
  const stuVid = useRef<HTMLVideoElement>(null);

  // ✅ Audio: même convention que MasterSessionVisualizer : refOffsetMs = -lagMs
  // ⚠️ IMPORTANT : l'offset s'applique UNIQUEMENT à l'audio ref (pas aux videos)
  const refOffsetMs = useMemo(() => -(lagMs || 0), [lagMs]);

  const { status, isPlaying, currentTime, togglePlay, seek, setMix } = useBufferAudioSync({
    stuUrl: stuUrl || "",
    refUrl: hasRefAudio ? refUrl : undefined,
    refOffsetMs,
    playbackSpeed: 1.0,
  } as any);

  // ---------------------------
  // VIDEO SYNC (event-driven, NO RAF)
  // Règle d’or : les 2 vidéos suivent le même currentTime (timeline audio master)
  // ---------------------------
  const DRIFT_MAX = 0.25; // sec
  const COOLDOWN_MS = 900;

  const lastWriteMsRef = useRef(0);
  const lastDriftFixMsRef = useRef(0);

  const forceVideoTime = useCallback(
    (t: number) => {
      const now = performance.now();
      if (now - lastWriteMsRef.current < 120) return;
      lastWriteMsRef.current = now;

      const tSafe = Math.max(0, Number.isFinite(t) ? t : 0);

      const write = (v?: HTMLVideoElement | null) => {
        if (!v || v.readyState < 2) return;
        try {
          const max =
            Number.isFinite(v.duration) && v.duration > 0 ? v.duration - 0.001 : 1e9;
          v.currentTime = clamp(tSafe, 0, max);
        } catch {}
      };

      if (hasStuVideo) write(stuVid.current);
      if (hasRefVideo) write(refVid.current);
    },
    [hasStuVideo, hasRefVideo]
  );

  const maybeFixDrift = useCallback(() => {
    if (!isPlaying) return;

    const now = performance.now();
    if (now - lastDriftFixMsRef.current < COOLDOWN_MS) return;

    const t = Math.max(0, Number.isFinite(currentTime) ? currentTime : 0);

    const check = (v?: HTMLVideoElement | null) => {
      if (!v || v.readyState < 2) return false;
      const d = t - v.currentTime;
      if (Math.abs(d) > DRIFT_MAX) {
        try {
          const max =
            Number.isFinite(v.duration) && v.duration > 0 ? v.duration - 0.001 : 1e9;
          v.currentTime = clamp(t, 0, max);
          return true;
        } catch {}
      }
      return false;
    };

    // ✅ Une seule correction par tick (évite stutter)
    if (check(stuVid.current) || check(refVid.current)) {
      lastDriftFixMsRef.current = now;
    }
  }, [isPlaying, currentTime]);

  // Forcer “pas de loop”
  useEffect(() => {
    const sv = stuVid.current;
    const rv = refVid.current;
    if (sv) sv.loop = false;
    if (rv) rv.loop = false;
  }, [hasStuVideo, hasRefVideo]);

  // Play/Pause mirror + align 1 fois au démarrage
  useEffect(() => {
    const sv = stuVid.current;
    const rv = refVid.current;

    const run = async () => {
      const t = Math.max(0, Number.isFinite(currentTime) ? currentTime : 0);

      if (hasStuVideo && sv) {
        try {
          sv.muted = true;
          sv.playsInline = true;
          sv.loop = false;
        } catch {}

        if (isPlaying) {
          if (sv.readyState >= 2) forceVideoTime(t);
          try { await sv.play(); } catch {}
        } else {
          try { sv.pause(); } catch {}
        }
      }

      if (hasRefVideo && rv) {
        try {
          rv.muted = true;
          rv.playsInline = true;
          rv.loop = false;
        } catch {}

        if (isPlaying) {
          if (rv.readyState >= 2) forceVideoTime(t);
          try { await rv.play(); } catch {}
        } else {
          try { rv.pause(); } catch {}
        }
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, hasStuVideo, hasRefVideo]);

  // Bind events (no RAF)
  useEffect(() => {
    const sv = stuVid.current;
    const rv = refVid.current;

    const handler = () => maybeFixDrift();
    const alignNow = () => forceVideoTime(Number.isFinite(currentTime) ? currentTime : 0);

    const bind = (v?: HTMLVideoElement | null) => {
      if (!v) return () => {};
      v.addEventListener("timeupdate", handler);
      v.addEventListener("waiting", handler);
      v.addEventListener("stalled", handler);
      v.addEventListener("seeked", handler);
      v.addEventListener("playing", handler);
      v.addEventListener("loadedmetadata", alignNow);

      // ✅ si ended : surtout ne pas boucler
      const onEnded = () => {
        try { v.pause(); } catch {}
      };
      v.addEventListener("ended", onEnded);

      return () => {
        v.removeEventListener("timeupdate", handler);
        v.removeEventListener("waiting", handler);
        v.removeEventListener("stalled", handler);
        v.removeEventListener("seeked", handler);
        v.removeEventListener("playing", handler);
        v.removeEventListener("loadedmetadata", alignNow);
        v.removeEventListener("ended", onEnded);
      };
    };

    const unStu = hasStuVideo ? bind(sv) : () => {};
    const unRef = hasRefVideo ? bind(rv) : () => {};

    return () => {
      unStu();
      unRef();
    };
  }, [hasStuVideo, hasRefVideo, maybeFixDrift, forceVideoTime, currentTime]);

  // ---------------------------
  // WAV (Recharts) — renderer identique AudioLab
  // ---------------------------
  const safeDuration = useMemo(() => {
    const d1 = typeof duration === "number" && isFinite(duration) ? duration : 0;
    const d2 = stuEnvelope && stuEnvelope.length > 0 ? (stuEnvelope.length * 256) / 44100 : 0;
    return Math.max(d1, d2, 0.001);
  }, [duration, stuEnvelope]);

  const safeDur = Math.max(0.001, safeDuration || 1);

  const [mixMode, setMixModeState] = useState<MixMode>("mix");
  // si ton hook expose setMix, on le relie au toggle ref/mix/stu
  useEffect(() => {
    if (!setMix) return;
    if (mixMode === "ref") setMix(0.0);
    else if (mixMode === "stu") setMix(1.0);
    else setMix(0.5);
  }, [mixMode, setMix]);

  const setMixMode = useCallback((m: MixMode) => setMixModeState(m), []);

  const chartData = useMemo(() => {
    const rLen = refEnvelope?.length || 0;
    const sLen = stuEnvelope?.length || 0;
    if (rLen === 0 && sLen === 0) return [];

    const steps = 1200;
    const data: any[] = [];

    const maxRef = rLen > 0 ? refEnvelope.reduce((a, b) => Math.max(a, b), 0.001) : 0.001;
    const maxStu = sLen > 0 ? stuEnvelope.reduce((a, b) => Math.max(a, b), 0.001) : 0.001;

    const refScale = 4.0 / maxRef;
    const stuScale = 4.0 / maxStu;

    for (let i = 0; i < steps; i++) {
      const p = i / (steps - 1);
      const time = p * safeDur;

      const rIdx = rLen > 1 ? Math.floor(p * (rLen - 1)) : 0;
      const sIdx = sLen > 1 ? Math.floor(p * (sLen - 1)) : 0;

      const rV = rLen > 0 ? (refEnvelope[rIdx] || 0) * refScale : 0;
      const sV = sLen > 0 ? (stuEnvelope[sIdx] || 0) * stuScale : 0;

      data.push({
        time,
        refP: isFinite(rV) ? rV : 0,
        refN: isFinite(rV) ? -rV : 0,
        stuP: isFinite(sV) ? sV : 0,
        stuN: isFinite(sV) ? -sV : 0,
      });
    }
    return data;
  }, [refEnvelope, stuEnvelope, safeDur]);

  const cursorPct = clamp(((Number.isFinite(currentTime) ? currentTime : 0) / safeDur) * 100, 0, 100);

  const seekAndAlign = useCallback(
    async (t: number) => {
      await seek(t);
      forceVideoTime(t);
    },
    [seek, forceVideoTime]
  );

  const toggleAndAlign = useCallback(async () => {
    const t = Number.isFinite(currentTime) ? currentTime : 0;
    if (!isPlaying) forceVideoTime(t);
    await togglePlay();
  }, [isPlaying, currentTime, togglePlay, forceVideoTime]);

  const onWaveClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      const x = clamp((e.clientX - rect.left) / Math.max(1, rect.width), 0, 1);
      const t = x * safeDur;
      void seekAndAlign(t);
    },
    [safeDur, seekAndAlign]
  );

  return (
    <div className="w-full h-full flex flex-col gap-4">
      {/* VIDEOS — format YouTube 16:9 */}
      {(hasRefVideo || hasStuVideo) && (
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden">
            {hasRefVideo ? (
              <div className="relative w-full aspect-video">
                <video
                  ref={refVid}
                  src={refVideoUrl}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-video flex items-center justify-center text-white/40">
                No ref video
              </div>
            )}
          </div>

          <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden">
            {hasStuVideo ? (
              <div className="relative w-full aspect-video">
                <video
                  ref={stuVid}
                  src={stuVideoUrl}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-video flex items-center justify-center text-white/40">
                No stu video
              </div>
            )}
          </div>
        </div>
      )}

      {/* CONTROLS */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={toggleAndAlign}
          className="px-5 py-3 rounded-full bg-white text-black font-black disabled:opacity-40"
          disabled={!hasStuAudio}
        >
          {isPlaying ? "⏸ Pause" : "▶ Play"}
        </button>

        <button
          onClick={() => void seekAndAlign(0)}
          className="px-4 py-3 rounded-full bg-white/10 text-white font-bold"
        >
          Reset
        </button>

        <div className="text-white/70 text-sm font-mono">
          {status} · t={Number.isFinite(currentTime) ? currentTime.toFixed(2) : "0.00"}s · lagMs={Number(lagMs || 0).toFixed(1)} · refOffsetMs={refOffsetMs.toFixed(1)}
        </div>

        <div className="ml-auto flex gap-2 p-1 bg-black/40 rounded-full border border-white/5">
          {(["ref", "mix", "stu"] as const).map((m) => (
            <button
              key={m}
              onClick={() => setMixMode(m)}
              className={`px-4 py-2 rounded-full text-[9px] font-black uppercase transition-all ${
                mixMode === m ? "bg-white text-black shadow-lg" : "text-slate-500 hover:text-white"
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* WAV — Recharts */}
      <div
        className="relative w-full flex-1 bg-[#010507] border border-white/10 rounded-2xl overflow-hidden cursor-pointer"
        onClick={onWaveClick}
        title={lang === "fr" ? "Clique pour te déplacer" : "Click to seek"}
      >
        <div className="absolute inset-0">
          <StaticSessionChart data={chartData} mixMode={mixMode} totalDuration={safeDur} />
        </div>

        <div
          className="absolute top-0 bottom-0 w-[2px] bg-white shadow-[0_0_20px_white] z-30 transition-all duration-75"
          style={{
            left: `${cursorPct}%`,
            transform: "translateX(-50%)",
          }}
        />
      </div>
    </div>
  );
};